import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import {
  importMeeshoManagementOrders,
  importMeeshoOutstandingPayments,
  importMeeshoPaymentsMarketingAndExpenses,
} from "app/redux/slice/meeshoOrderManagementSlice";
import { useNavigate } from "react-router-dom";
import {
  ORDER_MANAGEMENT_ORDER_HISTORY,
  ORDER_MANAGEMENT_PAYMENT_MARKETING_EXPENSES_HISTORY,
} from "app/routes/constant";
import { userType } from "app/helpers/userType";

const ImportPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isMA } = userType();

  const [activeTab, setActiveTab] = useState("orders");
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    historyPermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        historyPermission: true,
      });
    } else {
      let permissionValue = {
        historyPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "import-list") {
            permissionValue.historyPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  // file drop handler
  const onDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      if (activeTab === "orders") {
        // ✅ only CSV allowed
        if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
          enqueueSnackbar("Please select a valid CSV file", {
            variant: "error",
          });
          return;
        }
      } else {
        // ✅ only XLS or XLSX allowed
        const isExcel =
          file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
        if (!isExcel) {
          enqueueSnackbar("Please select a valid Excel (.xlsx/.xls) file", {
            variant: "error",
          });
          return;
        }
      }

      setSelectedFile(file);
      setImportResult(null);
    }
  };

  // accept type based on tab
  const acceptedTypes = activeTab === "orders" ? "text/csv,.csv" : ".xlsx,.xls";

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    multiple: false,
  });

  const handleImport = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please select a file first", { variant: "warning" });
      return;
    }

    setUploading(true);
    const formData = new FormData();

    if (activeTab === "orders") {
      formData.append("csvFile", selectedFile);
    } else {
      formData.append("file", selectedFile);
    }

    try {
      let response = {};
      if (activeTab === "orders") {
        response = await dispatch(
          importMeeshoManagementOrders(formData),
        ).unwrap();
      } else if (activeTab === "payments") {
        response = await dispatch(
          importMeeshoPaymentsMarketingAndExpenses(formData),
        ).unwrap();
      } else if (activeTab === "outstanding") {
        response = await dispatch(
          importMeeshoOutstandingPayments(formData),
        ).unwrap();
      }

      if (response?.data?.statusCode === 200) {
        setImportResult(response?.data?.data);
        enqueueSnackbar(
          `${
            activeTab === "orders"
              ? "Orders"
              : activeTab === "outstanding"
                ? "Outstanding Payments"
                : "Payments & Marketing And Expenses"
          } imported successfully!`,
          { variant: "success" },
        );
      } else {
        enqueueSnackbar(response?.data?.message || "Import failed", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to import data",
        { variant: "error" },
      );
    } finally {
      setUploading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedFile(null);
    setImportResult(null);
  };

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          marginTop: 0,
          paddingTop: 0,
        }}
      >
        <Tab value="orders" label="Order Import (CSV)" />
        <Tab value="payments" label="Payments & Marketing Expenses (Excel)" />
        <Tab value="outstanding" label="Outstanding Payments (Excel)" />
      </Tabs>

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box
                {...getRootProps()}
                sx={{
                  border: `2px dashed ${
                    isDragActive
                      ? theme.palette.primary.main
                      : theme.palette.grey[300]
                  }`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive
                    ? alpha(theme.palette.primary.main, 0.05)
                    : "transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <input {...getInputProps()} />
                <UploadIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" gutterBottom>
                  {isDragActive
                    ? `Drop the ${
                        activeTab === "orders" ? "CSV" : "Excel"
                      } file here`
                    : `Drag & drop a ${
                        activeTab === "orders" ? "CSV" : "Excel"
                      } file here`}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  or click to select a file
                </Typography>
                <Button variant="outlined" disabled={uploading}>
                  Select {activeTab === "orders" ? "CSV" : "Excel"} File
                </Button>
              </Box>

              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Selected File:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile.name} (
                      {(selectedFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                  </Paper>
                </Box>
              )}

              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Importing...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              <Box
                sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={!selectedFile || uploading}
                    startIcon={<UploadIcon />}
                  >
                    {uploading ? "Importing..." : "Import Data"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedFile(null);
                      setImportResult(null);
                    }}
                    disabled={uploading}
                  >
                    Clear
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      navigate(
                        activeTab === "orders"
                          ? ORDER_MANAGEMENT_ORDER_HISTORY
                          : ORDER_MANAGEMENT_PAYMENT_MARKETING_EXPENSES_HISTORY,
                      );
                    }}
                    disabled={!permission.historyPermission}
                  >
                    View History
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {activeTab === "orders"
                  ? "Order Import Instructions"
                  : activeTab === "outstanding"
                    ? "Outstanding Payments Import Instructions"
                    : "Payments & Marketing Expenses Import Instructions"}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {activeTab === "orders" ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    CSV Format Requirements:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                  >
                    • Sub Order No (required)
                    <br />
                    • Order Date (required)
                    <br />
                    • Customer State
                    <br />
                    • Product Name
                    <br />
                    • SKU
                    <br />
                    • Size
                    <br />
                    • Quantity
                    <br />
                    • Supplier Listed Price
                    <br />
                    • Supplier Discounted Price
                    <br />
                    • Packet Id
                    <br />• Reason for Credit Entry
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Duplicate orders (same Sub Order No & Status) will be
                    automatically skipped.
                  </Alert>
                </Box>
              ) : (
                ""
              )}

              {activeTab === "payments" ? (
                <>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Payments Excel Format Requirements:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Sub Order No (required)
                      <br />
                      • Order Date
                      <br />
                      • Product Name
                      <br />• supplier SKU
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Duplicate payments (same Payment ID) will be skipped.
                    </Alert>
                  </Box>
                  <Divider sx={{ mt: 2, mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Marketing & Expenses Excel Format Requirements:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Deduction Date (required)
                      <br />
                      • Deduction Duration Date (required)
                      <br />
                      • Ads cost (required)
                      <br />
                      • Total (required)
                      <br />
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Duplicate Marketing And Expenses will be skipped.
                    </Alert>
                  </Box>
                </>
              ) : (
                ""
              )}

              {activeTab === "outstanding" ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Meesho Outstanding Payment Excel:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Payments → Outstanding Payments download
                    <br />
                    • Import the Previous Payment file first, then this one
                    <br />• Use a file downloaded on the same day
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Every import replaces the previous outstanding data. Ads in
                    this file are estimates and are not imported.
                  </Alert>
                </Box>
              ) : (
                ""
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results */}
      {activeTab === "orders"
        ? importResult && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Import Results
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="primary">
                        {importResult.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Records
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="success.main">
                        {importResult.imported}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Imported
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="warning.main">
                        {importResult.duplicates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Duplicates
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="error.main">
                        {importResult?.skip ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        skip
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="error.main">
                        {importResult.errors}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Errors
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {importResult.errorDetails?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Error Details:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {importResult.errorDetails.map((error, index) => (
                        <Alert key={index} severity="error" sx={{ mb: 1 }}>
                          Row {error.row}: {error.error}
                        </Alert>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )
        : ""}

      {activeTab === "payments" || activeTab === "outstanding"
        ? importResult && (
            <>
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {activeTab === "outstanding"
                      ? "Outstanding Payments Import Results"
                      : "Payments Import Results"}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {importResult?.summary?.payments?.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Records
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="success.main">
                          {importResult?.summary?.payments?.imported}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Imported
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="warning.main">
                          {importResult?.summary?.payments?.duplicate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Duplicates
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="error.main">
                          {importResult?.summary?.payments?.issues?.length ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Errors
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {importResult?.summary?.payments?.issues?.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Error Details:
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                        {importResult?.summary?.payments?.issues?.map(
                          (error, index) => (
                            <Alert key={index} severity="error" sx={{ mb: 1 }}>
                              Row {error?.row}: {error?.error}
                            </Alert>
                          ),
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
              {activeTab === "payments" && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Marketing & Expenses Import Results
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h4" color="primary">
                            {importResult?.summary?.ads.total}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Records
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h4" color="success.main">
                            {importResult?.summary?.ads?.imported}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Imported
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h4" color="warning.main">
                            {importResult?.summary?.ads?.duplicate}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Duplicates
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h4" color="error.main">
                            {importResult?.summary?.ads?.issues?.length ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Errors
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {importResult?.summary?.ads?.issues?.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Error Details:
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                          {importResult?.summary?.ads?.issues?.map(
                            (error, index) => (
                              <Alert key={index} severity="error" sx={{ mb: 1 }}>
                                Row {error?.row}: {error?.error}
                              </Alert>
                            ),
                          )}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )
        : ""}
    </Box>
  );
};

export default ImportPage;
